import React, { Component } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import ReactTimeout from 'react-timeout';
import { deepLinkFavoriteSeries } from 'helpers/AccountHelper';
import { helperGetApiHeaders, helperConvertApiRoute } from 'helpers/ApiHelper';
import {
  getErrorComponent,
  isStaticRender,
  PAGE_TYPE_ARTICLE,
  PAGE_TYPE_CLIPS,
  PAGE_TYPE_CREDITS,
  slugify,
} from 'helpers/ContentHelper';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { noop } from 'helpers/GlobalHelper';
import { createSchemaTag, createSeriesDetailSchema } from 'helpers/SchemaHelper';
import Breadcrumbs, { breadcrumbsSeriesDetailParser } from 'components/Breadcrumbs';
import Icon from 'components/Icon';
import {
  makeSelectSeriesDetailInline,
  makeSelectSeriesDetailLatestEpisode,
  makeSelectSeriesDetailPagesSeo,
  makeSelectSeriesDetailPromos,
  makeSelectSeriesDetailRecommendedPanel,
  makeSelectSeriesDetailSeo,
  makeSelectSeriesDetailSeriesData,
  makeSelectSeriesDetailTabs,
} from 'screens/SeriesDetail/selectors';
import {
  clearSeriesDetail,
  fetchSeries,
  fetchV2SEO,
  setSeriesDetailError,
} from 'screens/SeriesDetail/SeriesDetailActions';
import { fetchPanel, fetchNextPanelPage } from 'components/Panel/PanelActions';
import { fetchBioLanding, fetchBioDetail } from 'screens/SeriesDetail/components/Bio/BioActions';
import {
  Events,
  getFavoritesListString,
  getRecommendationId,
  getTealiumDetailTabData,
  getTealiumSourceCollectionGlobal,
  handleAnalytics,
  PageNames,
  setTealiumSourceCollectionGlobal,
  trackPageReferrerAnalytics,
} from '@foxcorp/delta-web-analytics';
import {
  ARTICLE_SCROLL_ELEMENT,
  getFeaturedBio,
  getTabDataUrl,
  getTabName,
  getTabsOfMultipleTypes,
  PANEL_TYPE_CLIPS,
  PANEL_TYPE_SCREEN_PANEL,
} from 'screens/SeriesDetail/SeriesDetailHelpers';
import { setRecommendationIdAction as setRecommendationId } from 'index/AppActions';

import { fetchArticle } from 'components/Article/ArticleActions';
import { getArticleApiUrl, getArticleFromAlternateUrl } from 'components/Article/ArticleHelpers';
import { redirectToExternalUrl } from 'helpers/RouteHelper';
import Article from 'components/Article';
import NFTBanner from './components/NFTBanner';
import NFTModule from './components/NFTModule';
import About from './components/AboutV2';
import Background from './components/Background';

import Cast from './components/Cast';
import CastDetail from './components/Cast/CastDetail';
import FoxContentDensitySkeleton from './components/FoxContentDensitySkeleton';
import Hero from './components/Hero';
import Clips from './components/Clips';
import InfoContainer from './components/InfoContainer';
import Seasons from './components/Seasons';
import Links from './components/Links';
import SeeAll from './components/SeeAll';
import Recommended from './components/Recommended';
import Divider from './components/Divider';
import Style from './FoxContentDensity.module.scss';

const FETCH_NEW_CONTENT_INTERVAL_MS = 1000 * 60 * 30;
let isInitialized = false;

class ContentDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      castArray: [],
      // Potentially needed for Favorites in hero
      // eslint-disable-next-line react/no-unused-state
      deepLinkFavorited: false,
      // trackedAnalyticsReferrer is used in trackPageReferrerAnalytics
      // eslint-disable-next-line react/no-unused-state
      trackedAnalyticsReferrer: false,
    };

    this.deepLinkFavoriteSeries = deepLinkFavoriteSeries.bind(this);
    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
  }

  componentDidMount() {
    const { params } = this.props;
    const { seriesRedirectMap } = window?.ExternalConfig || {};
    if (!isInitialized) {
      isInitialized = true;
      this.fetchInitialData();
    }
    this.trackPageView();
    if (!isStaticRender() && seriesRedirectMap?.[params?.showId]) {
      redirectToExternalUrl(seriesRedirectMap[params.showId]);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      accessToken: prevAccessToken,
      params: { showId: prevShowId, pageType: prevPageType, pageId: prevPageId },
      sections: prevSections,
      errors: prevErrors,
    } = prevProps;

    const {
      accessToken,
      clearSeriesDetail,
      deviceId,
      errors,
      fetchBioLanding,
      fetchSeries,
      fetchV2SEO,
      params: { showId, pageType, pageId },
      sections,
      seriesType,
      seriesData,
    } = this.props;

    const fetchNewSeries = prevShowId !== showId && prevShowId && showId;
    const newPageType = prevPageType !== pageType;
    const loginChanged = accessToken !== prevAccessToken;
    const newErrors = !prevErrors && errors;
    const pageIdChanged = prevPageId !== pageId;
    const newSections = !prevSections && !!sections;
    const newPageIds = !prevPageId && !!pageId;
    const firePageView = newSections || newErrors;
    if (sections) {
      const seasons = _.filter(sections, section => {
        const sectionType = _.get(section, '@type', null);
        return sectionType === 'Season';
      });
      const seasonPage =
        pageType && _.find(seasons, season => season && pageType?.includes(season.url));
      const isClips = pageType === PAGE_TYPE_CLIPS;
      if ((newSections || newPageType) && seasonPage) {
        const url = seasonPage?.SEOV2?.['@id'];
        if (url) fetchV2SEO(url, accessToken, deviceId);
      }
      if ((newSections || newPageType || pageIdChanged || newPageIds) && isClips) {
        const clipsSections = getTabsOfMultipleTypes(sections, [
          PANEL_TYPE_CLIPS,
          PANEL_TYPE_SCREEN_PANEL,
        ])?.map(section => {
          const { id } = section;
          const name = getTabName({ tab: section, seriesType });
          return { ...section, name, showCode: seriesData.showCode, id, url: id };
        });
        const clipPage = _.find(clipsSections, item => slugify(item.name) === pageId);
        const url = clipPage?.SEOV2?.['@id'];
        if (url) fetchV2SEO(url, accessToken, deviceId);
      }
    }
    if (newSections) {
      fetchBioLanding(showId, accessToken, deviceId);
    }
    if (fetchNewSeries) {
      clearSeriesDetail();
      fetchSeries(showId, accessToken, deviceId);
    }
    if (loginChanged) {
      fetchSeries(showId, accessToken, deviceId);
    }
    if (firePageView) this.trackPageView();
  }

  componentWillUnmount() {
    const { clearSeriesDetail, disableScroll } = this.props;
    isInitialized = false;
    this.trackPageReferrerAnalytics({
      name: PageNames.SERIES_DETAIL,
    });
    clearSeriesDetail();
    disableScroll({ disableScroll: false, bodyOnly: false });
  }

  setErrors = errors => {
    setSeriesDetailError(errors);
  };

  setCastArray = castArray => {
    this.setState({ castArray });
  };

  setFetchInterval = fetch => {
    const {
      params: { showId },
      accessToken,
      deviceId,
      setInterval,
    } = this.props;
    fetch(showId, accessToken, deviceId);
    setInterval(() => {
      const {
        accessToken: currentAccessToken,
        deviceId: currentDeviceId,
        params: { showId },
      } = this.props;
      fetch(showId, currentAccessToken, currentDeviceId);
    }, FETCH_NEW_CONTENT_INTERVAL_MS);
  };

  fetchInitialData() {
    if (isStaticRender()) return;
    const { fetchSeries } = this.props;
    this.setFetchInterval(fetchSeries);
  }

  renderArticle(article) {
    const {
      params: { pageType, pageId },
      accessToken,
      deviceId,
      pageViewEvent,
      setLoading,
    } = this.props;
    if (!article && (!pageId || pageType !== PAGE_TYPE_ARTICLE)) return null;
    const articleId = _.get(article, 'articleId', null);
    const articleUrl = getArticleApiUrl(pageId || articleId);
    return (
      <div className={Style.article}>
        <Article
          disableScrollEvent
          url={articleUrl}
          accessToken={accessToken}
          deviceId={deviceId}
          pageViewEvent={pageViewEvent}
          scrollElement={ARTICLE_SCROLL_ELEMENT}
          setErrors={this.setErrors}
          setLoading={setLoading}
        />
      </div>
    );
  }

  renderCastDetail = ({ currentCast, castArray, id, contentCode, isCast = true }) => {
    const { accessToken, breakpoint } = this.props;
    const index = _.findIndex(castArray, { alternateId: id });
    const renderArrows = castArray.length !== 1;
    const nextCast = index === castArray.length - 1 ? castArray[0] : castArray[index + 1];
    const prevCast = index === 0 ? castArray[castArray.length - 1] : castArray[index - 1];
    const nextArrow = renderArrows ? (
      <Link
        className={Style.nextCastArrow}
        to={`/${contentCode}/${isCast ? 'credits' : 'characters'}/${nextCast.alternateId}/`}
        onlyActiveOnIndex
      >
        <Icon color="white" size="large" viewBox="0 0 18 32" icon="ios-arrow-right" />
      </Link>
    ) : null;

    const prevArrow = renderArrows ? (
      <Link
        className={Style.prevCastArrow}
        to={`/${contentCode}/${isCast ? 'credits' : 'characters'}/${prevCast.alternateId}/`}
        onlyActiveOnIndex
      >
        <Icon color="white" size="large" viewBox="0 0 18 32" icon="ios-arrow-left" />
      </Link>
    ) : null;

    return (
      <div className={Style.castDetail}>
        <CastDetail
          accessToken={accessToken}
          breakpoint={breakpoint}
          detail={currentCast}
          nextArrow={nextArrow}
          prevArrow={prevArrow}
        />
      </div>
    );
  };

  trackPageView = () => {
    const {
      pageViewEvent,
      sections,
      seriesData,
      seriesType,
      getUseUtagGlobal,
      errors,
      favoritesManager: { favorites },
    } = this.props;
    if (!seriesData) return;
    const section = _.get(sections, '0');
    const pageTitle = _.get(seriesData, 'name');
    const referringCollection = getTealiumSourceCollectionGlobal();
    const pageType = `detail:${seriesType}`;
    const seriesCollection = getTealiumDetailTabData({ seriesData, tab: section });
    const selectedMenuItem = _.get(seriesCollection, 'selectedMenuItem');

    if ((section || errors) && pageViewEvent) {
      if (referringCollection) {
        const { name: title } = referringCollection;
        setTealiumSourceCollectionGlobal({ ...referringCollection, title });
      }

      const collection = referringCollection;

      pageViewEvent({
        pageName: PageNames.DETAIL,
        collection,
        useUtagGlobal: getUseUtagGlobal(),
        seriesType,
        pageTitle,
        pageType: `${pageType}:landing`,
        selectedMenuItem,
      });
      // Fire after page view event
      const hasFavorite = !!_.filter(favorites, { showCode: seriesData.showCode })[0];
      if (!hasFavorite)
        this.deepLinkFavoriteSeries({
          props: this.props,
          setDeepLinkFavorited: this.setDeepLinkFavorited,
        });
    }

    handleAnalytics(
      {
        event: Events.DETAIL_PAGE_MENU_ITEM_VIEW,
        payload: {
          pageName: `detail:${seriesType}:${pageTitle}:${selectedMenuItem}`,
          pageType: `detail:${pageType}:menu`,
          selectedMenuItem,
        },
      },
      {
        event: Events.DETAIL_PAGE_DEFAULT_STATE,
        payload: {
          pageName: `${pageType}:${pageTitle}`,
          pageType: `${pageType}:landing`,
          pageCollectionType: referringCollection
            ? referringCollection.collectionType
            : 'no collection type',
          pageCollectionTitle: referringCollection ? referringCollection.name : null,
          ...(favorites && {
            favoritesContentLength: favorites.length,
            favoritesContentList: getFavoritesListString(favorites),
          }),
        },
      },
    );
  };

  getBreadcrumbsData() {
    const { params, seriesData, links, bioProfiles } = this.props;
    const { pageId, pageType } = params;
    const articleData = getArticleFromAlternateUrl({ articles: links, altUrl: pageType });
    const creditsData = getFeaturedBio(pageId, bioProfiles);
    const breadcrumbsData = breadcrumbsSeriesDetailParser(
      params,
      seriesData,
      articleData,
      creditsData,
    );
    return breadcrumbsData;
  }

  fetchPanelData = (url, headers, panelId) => {
    const { fetchPanel } = this.props;
    fetchPanel(url, headers, panelId);
  };

  render() {
    const {
      accessToken,
      breakpoint,
      castLandingSeo,
      continueWatchingItems,
      deviceId,
      disableScroll,
      episodes,
      errors,
      links,
      favoritableItems,
      favoritesManager,
      fetchNextPanelPage,
      inline,
      isAnonymous,
      isAuthenticated,
      pagesSeo,
      panels,
      params: { pageType, showId = '', pageId },
      recommended,
      recommendationIdData,
      sections,
      seriesSeo,
      seriesData,
      seriesSortOrder,
      seriesType,
      articleSeo,
    } = this.props;
    const { castArray } = this.state;
    const articles = _.filter(links, 'isArticle');

    const seasons = _.filter(sections, section => {
      const sectionType = _.get(section, '@type', null);
      return sectionType === 'Season';
    });

    const item = _.get(inline, 'items.member.0', null);

    const { name: contentName, showCode: contentCode } = seriesData || {};

    const castInfo = _.find(sections, { '@type': 'Cast' })?.member ?? null;
    const isSameShowAsTheUrlParam = contentCode === showId;
    const showLoader = !isSameShowAsTheUrlParam || (!inline && !sections);

    const recommendationId = getRecommendationId(recommendationIdData);
    if (errors) return getErrorComponent(errors);

    if (!seriesData || showLoader) return <FoxContentDensitySkeleton />;

    const heroProps = {
      breakpoint,
      continueWatchingItems,
      episodes,
      disableScroll,
      isAuthenticated,
      item,
      favoritesManager,
      favoritableItems,
      recommendationId,
    };

    const commonProps = {
      accessToken,
      breakpoint,
      deviceId,
      fetchPanelData: this.fetchPanelData,
      isAnonymous,
      pageType,
      seriesType,
    };

    const seasonProps = {
      ...commonProps,
      fetchNextPanelPage,
      inline,
      panels,
      seasons,
    };

    const recommendedProps = {
      ...commonProps,
      favoritesManager,
      recommended,
    };

    const clipsProps = {
      ...commonProps,
      inline,
      panels,
      sections,
      seriesName: contentName,
      showCode: contentCode,
    };

    const infoContainerProps = {
      breadcrumbsData: this.getBreadcrumbsData(),
      showCode: contentCode,
      showName: contentName,
    };

    const castProps = {
      castInfo,
      disableScroll,
      breakpoint,
      contentCode,
      setCastArray: this.setCastArray,
    };

    // TODO: use Selectors to pull these props into the components independently in favor of passing them. (where possible)
    const getSection = (item, index) => {
      const { section, type: sectionType } = item;
      const hasNFTCountdown = seriesSortOrder?.find(elem => elem?.type === 'nftCountdown');
      switch (sectionType) {
        case 'season':
          return <Seasons key={`${item.id} ${index}`} {...seasonProps} />;
        case 'clips':
          return <Clips key={`${item.id} ${index}`} {...clipsProps} sections={[section]} />;
        case 'about':
          return <About key={`${item.id} ${index}`} />;
        case 'promo':
          return <Links key={`${item.id} ${index}`} links={links} accessToken={accessToken} />;
        case 'cast':
          return <Cast key={`${item.id} ${index} Cast`} {...castProps} />;
        case 'nftRewards':
          return (
            <>
              <NFTBanner
                ctaText={section?.ctaText}
                description={section?.description}
                header={section?.headline}
                images={section?.images}
                rewardUrl={section?.url}
              />
              {!hasNFTCountdown && <Divider />}
            </>
          );
        case 'nftCountdown':
          return (
            <>
              <NFTModule
                ctaText={section?.ctaText}
                dropUrl={section?.url}
                endDate={section?.countdownTo}
                images={section?.images}
                videos={section?.autoPlayVideo}
                nftName={section?.headline}
              />
              <Divider />
            </>
          );
        default:
          return null;
      }
    };
    const detailScreen = (
      <>
        <Hero {...heroProps} />
        {seriesSortOrder?.map((item, index) => getSection(item, index))}
        <Recommended {...recommendedProps} />
      </>
    );

    let castMemberInfo = _.find(castArray, { alternateId: pageId });
    if (castInfo) {
      Object.keys(castInfo).map(key => {
        if (!castMemberInfo) {
          castMemberInfo = _.find(castInfo[key], { alternateId: pageId });
        }
      });
    }
    let pageContent;
    const selectedArticle =
      !_.isEmpty(articles) && pageType
        ? getArticleFromAlternateUrl({ articles, altUrl: pageType })
        : null;
    let seo = seriesSeo;
    let canonical = seo?.canonicalUrl;
    const isSeasonPage =
      pageType && !!_.find(seasons, season => season && pageType?.includes(season.url));
    const defaultContent = () => {
      if (selectedArticle) {
        seo = { ...seo, ...articleSeo };
        return (
          <div className={Style.infoContainer}>
            <InfoContainer {...infoContainerProps}>
              {this.renderArticle(selectedArticle)}
            </InfoContainer>
          </div>
        );
      }
      if (isSeasonPage) {
        seo = {
          ...seo,
          ...pagesSeo,
          canonicalUrl: `${canonical}${pageType ? `${pageType}/` : ''}`,
        };
      }
      return (
        <>
          <Background item={item} breakpoint={breakpoint} />
          <div className={Style.contentContainer}>{detailScreen}</div>
        </>
      );
    };
    switch (pageType) {
      case PAGE_TYPE_ARTICLE:
        pageContent = (
          <div className={Style.infoContainer}>
            <InfoContainer {...infoContainerProps}>{this.renderArticle()}</InfoContainer>
          </div>
        );
        break;
      case PAGE_TYPE_CLIPS:
        if (canonical) {
          seo = { ...seo, canonicalUrl: `${canonical}${pageType}/${pageId ? `${pageId}/` : ''}` };
        }
        pageContent = (
          <>
            <Background item={item} breakpoint={breakpoint} />
            <Clips {...clipsProps} />
          </>
        );
        break;
      case PAGE_TYPE_CREDITS:
        if (castArray.length <= 0 && !castInfo) {
          pageContent = defaultContent();
          break;
        }
        seo = { ...seo, ...castLandingSeo };
        canonical = seo?.canonicalUrl;
        if (canonical) {
          seo = {
            ...seo,
            ...pagesSeo,
            canonicalUrl: `${canonical}${pageType}/${pageId ? `${pageId}/` : ''}`,
          };
        }
        pageContent = (
          <>
            <Background item={item} breakpoint={breakpoint} />
            <SeeAll
              breadcrumbsData={this.getBreadcrumbsData()}
              breakpoint={breakpoint}
              contentCode={contentCode}
              contentName={contentName}
              disableScroll={disableScroll}
              items={castArray}
              castInfo={castInfo}
              seriesType={seriesType}
              pageTitle={contentName}
            />
          </>
        );
        if (pageId) {
          const { bioProfiles } = this.props;
          if (!castMemberInfo) break;
          const { id } = castMemberInfo;
          const currentBioProfile = _.get(bioProfiles, id, {});
          const { seo: bioSeo, featured } = currentBioProfile;
          seo = { ...bioSeo };
          const { canonicalUrl } = seo;
          if (canonicalUrl) {
            seo.canonicalUrl = canonicalUrl
              ?.split('/')
              ?.map(item => (item === id ? featured.alternateId : item))
              ?.join('/');
          }
          pageContent = (
            <div className={Style.infoContainer}>
              <InfoContainer {...infoContainerProps}>
                {this.renderCastDetail({
                  currentCast: castMemberInfo,
                  castArray:
                    castArray?.length > 0
                      ? castArray
                      : castInfo && castInfo[castMemberInfo?.category],
                  id: pageId,
                  contentCode,
                })}
              </InfoContainer>
            </div>
          );
        }
        break;
      default:
        pageContent = defaultContent();
    }
    return (
      <>
        {setHelmetTags(seo)}
        {setHelmetTags({
          tags: [
            createSchemaTag({
              item: { ...seriesData, actors: _.get(castInfo, 'member', []) },
              getSchema: createSeriesDetailSchema,
            }),
          ],
        })}
        {pageContent}
        {pageType !== PAGE_TYPE_ARTICLE && pageType !== PAGE_TYPE_CREDITS && !selectedArticle && (
          <Breadcrumbs data={this.getBreadcrumbsData()} customStyle={Style.breadcrumbs} />
        )}
      </>
    );
  }
}

ContentDetail.contextTypes = {
  router: PropTypes.shape({}),
};

ContentDetail.defaultProps = {
  accessToken: null,
  account: null,
  adblock: false,
  anonymousId: null,
  articlePanels: null,
  authCheckComplete: false,
  authSuccess: false,
  bioProfiles: null,
  castLandingSeo: null,
  continueWatching: null,
  continueWatchingItems: [],
  deviceId: null,
  disableScroll: noop,
  errors: null,
  favoritableItems: [],
  favoritesManager: {},
  inline: {},
  isAnonymous: false,
  isAuthenticated: false,
  isHeader: false,
  isLatestEpisodeLive: null,
  latestEpisode: null,
  mvpdData: null,
  openSignIn: noop,
  openSignUp: noop,
  overlay: null,
  pagesSeo: {},
  preauthorizedResources: [],
  profileId: null,
  promos: [],
  provider: {},
  recommended: {},
  recommendationIdData: null,
  renderAuthContainer: noop,
  route: {},
  saveLiveVideo: noop,
  sections: [],
  seriesSeo: null,
  seriesData: null,
  seriesSortOrder: null,
  seriesType: null,
  setAccountCloseIgnoreRedirect: noop,
  setIsAuthenticated: noop,
  setIsPlayerLoaded: noop,
  setLoading: null,
  setMVPDLogoutDataToAppState: noop,
  setMVPDToAppState: noop,
  setProvider: noop,
  toggleMvpdAuthOverlay: noop,
};

ContentDetail.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  articleSeo: PropTypes.shape({}),
  adblock: PropTypes.bool,
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func.isRequired,
  articlePanels: PropTypes.shape({}),
  authCheckComplete: PropTypes.bool,
  authSuccess: PropTypes.bool,
  bioProfiles: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  castLandingSeo: PropTypes.shape({}),
  clearSeriesDetail: PropTypes.func.isRequired,
  continueWatching: PropTypes.shape({}),
  continueWatchingItems: PropTypes.arrayOf(PropTypes.shape({})),
  deviceId: PropTypes.string,
  disableScroll: PropTypes.func,
  errors: PropTypes.number,
  episodes: PropTypes.arrayOf(PropTypes.shape({})),
  favoritableItems: PropTypes.arrayOf(PropTypes.shape({})),
  favoritesManager: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    setFavorites: PropTypes.func.isRequired,
    deleteFavorites: PropTypes.func.isRequired,
  }),
  fetchArticle: PropTypes.func.isRequired,
  fetchBioLanding: PropTypes.func.isRequired,
  fetchNextPanelPage: PropTypes.func.isRequired,
  fetchPanel: PropTypes.func.isRequired,
  fetchSeries: PropTypes.func.isRequired,
  fetchV2SEO: PropTypes.func.isRequired,
  getUseUtagGlobal: PropTypes.func.isRequired,
  inline: PropTypes.shape({}),
  isAnonymous: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isHeader: PropTypes.bool,
  isLatestEpisodeLive: PropTypes.bool,
  latestEpisode: PropTypes.shape({}),
  links: PropTypes.arrayOf(PropTypes.shape({})),
  mvpdData: PropTypes.shape({}),
  openSignIn: PropTypes.func,
  openSignUp: PropTypes.func,
  overlay: PropTypes.string,
  pageViewEvent: PropTypes.func.isRequired,
  pagesSeo: PropTypes.shape({}),
  panels: PropTypes.shape({}),
  params: PropTypes.shape({
    showId: PropTypes.string,
  }).isRequired,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  profileId: PropTypes.string,
  promos: PropTypes.arrayOf(PropTypes.shape({})),
  provider: PropTypes.shape({}),
  recommended: PropTypes.shape({}),
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  renderAuthContainer: PropTypes.func,
  removeVideoHero: PropTypes.func.isRequired,
  route: PropTypes.shape({}),
  saveLiveVideo: PropTypes.func,
  seriesSeo: PropTypes.shape({}),
  seriesData: PropTypes.shape({}),
  seriesSortOrder: PropTypes.arrayOf(PropTypes.shape({})),
  seriesType: PropTypes.string,
  setAccountCloseIgnoreRedirect: PropTypes.func,
  setAuthorizationToken: PropTypes.func.isRequired,
  setInterval: PropTypes.func.isRequired,
  setIsAuthenticated: PropTypes.func,
  setIsPlayerLoaded: PropTypes.func,
  setLoading: PropTypes.func,
  setMVPDLogoutDataToAppState: PropTypes.func,
  setMVPDToAppState: PropTypes.func,
  setOverlay: PropTypes.func.isRequired,
  setProvider: PropTypes.func,
  setRecommendationId: PropTypes.func.isRequired,
  setVideoHero: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  toggleMvpdAuthOverlay: PropTypes.func,
};

function mapStateToProps(state) {
  const panels = _.get(state, 'panel.panels');
  return {
    articlePanels: state.article.panels,
    articleSeo: state.article.seo,
    bioProfiles: state.bio.profiles,
    castLandingSeo: state.bio.landingSeo,
    continueWatchingItems: state.seriesDetail.continueWatchingItems,
    episodes: state.seriesDetail.episodes,
    errors: state.seriesDetail.errors,
    favoritableItems: state.seriesDetail.favoritableItems,
    inline: makeSelectSeriesDetailInline(state),
    latestEpisode: makeSelectSeriesDetailLatestEpisode(state),
    links: state.seriesDetail.links,
    panels,
    promos: makeSelectSeriesDetailPromos(state),
    recommended: makeSelectSeriesDetailRecommendedPanel(state),
    recommendationIdData: state.app.recommendationIdData,
    sections: makeSelectSeriesDetailTabs(state),
    seriesData: makeSelectSeriesDetailSeriesData(state),
    seriesSeo: makeSelectSeriesDetailSeo(state),
    pagesSeo: makeSelectSeriesDetailPagesSeo(state),
    seriesSortOrder: state.seriesDetail.seriesSortOrder,
    seriesType: state.seriesDetail.seriesType,
  };
}

ContentDetail.fetchData = ({ store, params, accessToken }) => {
  const { showId, pageType, pageId } = params;
  isInitialized = true;
  return store
    .dispatch(dispatch => dispatch(fetchSeries(showId, accessToken)))
    .then(() => {
      const state = store.getState();
      const { tabs: sections, links, seriesType, seriesData } = state.seriesDetail;
      const articles = _.filter(links, 'isArticle');
      const selectedArticle =
        pageType && !_.isEmpty(articles)
          ? getArticleFromAlternateUrl({ articles, altUrl: pageType })
          : null;
      if (selectedArticle) {
        return store.dispatch(fetchArticle(getArticleApiUrl(selectedArticle.url), accessToken));
      }
      if (pageType === PAGE_TYPE_CREDITS) {
        if (pageId) {
          const castInfo = _.find(sections, { '@type': 'Cast' });
          const castArray = _.get(castInfo, 'member', []);
          const castMemberInfo = _.find(castArray, { alternateId: pageId });
          if (castMemberInfo) {
            const { id } = castMemberInfo;
            return store.dispatch(fetchBioDetail(id, accessToken));
          }
        } else {
          return store.dispatch(fetchBioLanding(showId, accessToken));
        }
      }

      if (pageType === PAGE_TYPE_CLIPS) {
        const clipsSections = getTabsOfMultipleTypes(sections, [
          PANEL_TYPE_CLIPS,
          PANEL_TYPE_SCREEN_PANEL,
        ])?.map(section => {
          const { id } = section;
          const name = getTabName({ tab: section, seriesType });
          return { ...section, name, showCode: seriesData.showCode, id, url: id };
        });
        const clipPage = _.find(clipsSections, item => slugify(item.name) === pageId);
        const url = clipPage?.SEOV2?.['@id'];
        if (url) store.dispatch(fetchV2SEO(url, accessToken));
      }

      const seasons = _.filter(sections, section => {
        const sectionType = _.get(section, '@type', null);
        return sectionType === 'Season';
      });
      const seasonPage =
        pageType && _.find(seasons, season => season && pageType?.includes(season.url));
      if (seasonPage) {
        const url = seasonPage?.SEOV2?.['@id'];
        if (url) store.dispatch(fetchV2SEO(url, accessToken));
      }

      if (_.isEmpty(seasons)) return store;
      let url;
      let panelId;
      if (seasonPage) {
        url = seasonPage?.episodes?.['@id'];
        panelId = _.get(seasonPage, '@id', url);
      } else {
        const [season] = seasons;
        const { '@id': id } = season;
        const panelUrl = getTabDataUrl(season);
        panelId = _.get(season, '@id', panelUrl);
        url = panelUrl ? helperConvertApiRoute(panelUrl) : helperConvertApiRoute(id);
      }
      const headers = helperGetApiHeaders({ pathKey: pageType, accessToken });
      return store.dispatch(fetchPanel(url, headers, panelId));
    });
};

export default ReactTimeout(
  connect(mapStateToProps, {
    clearSeriesDetail,
    fetchArticle,
    fetchBioLanding,
    fetchNextPanelPage,
    fetchPanel,
    fetchSeries,
    fetchV2SEO,
    setRecommendationId,
    setSeriesDetailError,
  })(ContentDetail),
);
